import { useCallback, useEffect, useState } from "react";
import { fetchFoodBasketCoverageBaseline } from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

const FoodAraBasketCoverageBaselineSection = ({ sourceKey }) => {
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const loadBaseline = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setBaseline(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodBasketCoverageBaseline({ sourceKey });
      setBaseline(payload);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load coverage baseline.");
      setBaseline(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    loadBaseline();
  }, [loadBaseline]);

  const summary = baseline?.summary;

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Basket Coverage Baseline</h2>
          <p className="muted">
            Read-only diagnostic: staple gaps between {sourceLabel} observations, normalization,
            and the latest Basic Basket calculation. Does not run ingestion or normalization.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadBaseline} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh baseline"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      {loading && !baseline ? <p className="muted">Loading baseline…</p> : null}

      {baseline ? (
        <>
          <div className="food-retail-ingestion-page__kpi-row">
            <div>
              <span className="muted">Raw observations</span>
              <strong>{summary.rawObservationCount}</strong>
            </div>
            <div>
              <span className="muted">Normalized</span>
              <strong>{summary.normalizedObservationCount}</strong>
            </div>
            <div>
              <span className="muted">Priced items</span>
              <strong>
                {summary.pricedItemCount} / {summary.basketItemCount}
              </strong>
            </div>
            <div>
              <span className="muted">Coverage</span>
              <strong>{summary.coveragePercent}%</strong>
            </div>
            <div>
              <span className="muted">Latest status</span>
              <strong>{summary.latestBasketStatus || "—"}</strong>
            </div>
          </div>

          {baseline.limitationNote ? (
            <p className="muted food-retail-ingestion-page__hint">{baseline.limitationNote}</p>
          ) : null}

          {baseline.recommendedNextTargets?.length ? (
            <div className="food-retail-ingestion-page__hint">
              <strong>Recommended next targets</strong>
              <ul>
                {baseline.recommendedNextTargets.map((target) => (
                  <li key={target.canonicalProductName}>
                    [{target.priority}] {target.canonicalProductName}: {target.gapReason} —{" "}
                    {target.recommendedAction}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Staple</th>
                  <th>Raw</th>
                  <th>Normalized</th>
                  <th>Fresh</th>
                  <th>Priced</th>
                  <th>Gap</th>
                  <th>Latest product</th>
                </tr>
              </thead>
              <tbody>
                {baseline.items.map((item) => (
                  <tr key={item.canonicalProductName}>
                    <td>{item.canonicalProductName}</td>
                    <td>{item.hasRawObservation ? "Yes" : "No"}</td>
                    <td>{item.hasNormalizedObservation ? "Yes" : "No"}</td>
                    <td>{item.hasFreshObservation ? "Yes" : "No"}</td>
                    <td>{item.pricedInLatestBasket ? "Yes" : "No"}</td>
                    <td>{item.gapReason}</td>
                    <td>{item.latestRawProductName || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </section>
  );
};

export default FoodAraBasketCoverageBaselineSection;
